import {isLoggedIn} from '@github-ui/client-env'
import {CodingAssistantButton} from '@github-ui/coding-assistant-button'
import {IssueMarkdownViewer} from '@github-ui/commenting/IssueMarkdownViewer'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ReactionViewerAnchor} from '@github-ui/reaction-viewer/ReactionViewerAnchor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {AddSubIssueButtonGroup} from '@github-ui/sub-issues/AddSubIssueButtonGroup'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import type {TaskItem} from '@github-ui/use-tasklist'
import {useSafeTimeout} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useMemo} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {ERRORS} from '../constants/errors'
import {commitCreateIssueFromChecklistItemMutation} from '../mutations/create-issue-from-checklist-item-mutation'
import {commitCreateSubIssueFromChecklistItemMutation} from '../mutations/create-sub-issue-from-checklist-item-mutation'
import {commitUpdateIssueBodyMutation} from '../mutations/update-issue-body-mutation'
import type {IssueBodyViewer$key} from './__generated__/IssueBodyViewer.graphql'
import type {IssueBodyViewerPinnedComment$key} from './__generated__/IssueBodyViewerPinnedComment.graphql'
import type {IssueBodyViewerPinnedCommentSecondary$key} from './__generated__/IssueBodyViewerPinnedCommentSecondary.graphql'
import type {IssueBodyViewerReactable$key} from './__generated__/IssueBodyViewerReactable.graphql'
import type {IssueBodyViewerSubIssues$key} from './__generated__/IssueBodyViewerSubIssues.graphql'
import styles from './IssueBodyViewer.module.css'
import {PinnedCommentDisplay} from './PinnedCommentDisplay'

const ReactionViewerRelay = React.lazy(() => import('@github-ui/reaction-viewer/ReactionViewerRelay'))

export type IssueBodyViewerProps = {
  html: SafeHTMLString
  markdown: string
  comment: IssueBodyViewer$key
  onLinkClick?: (event: MouseEvent) => void
  issueBodyRef?: React.RefObject<HTMLDivElement | null>
  bodyVersion: string
  viewerCanUpdate: boolean
  locked: boolean
  reactable: IssueBodyViewerReactable$key
  subIssues?: IssueBodyViewerSubIssues$key | null
  pinnedComment?: IssueBodyViewerPinnedComment$key | null
  pinnedCommentSecondary?: IssueBodyViewerPinnedCommentSecondary$key | null
  insideSidePanel?: boolean
  repositoryId?: string
  onIssueEditStateChange?: (edited: boolean) => void
  isReadonly: boolean
  issueUrl?: string
}

export function IssueBodyViewer({
  html,
  markdown,
  comment,
  onLinkClick,
  issueBodyRef,
  bodyVersion,
  locked,
  viewerCanUpdate,
  reactable,
  subIssues = null,
  pinnedComment = null,
  pinnedCommentSecondary = null,
  insideSidePanel,
  repositoryId,
  onIssueEditStateChange,
  isReadonly,
  issueUrl,
}: IssueBodyViewerProps) {
  const environment = useRelayEnvironment()
  const {safeSetTimeout, safeClearTimeout} = useSafeTimeout()
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyViewer on Comment {
        id
      }
    `,
    comment,
  )
  const dataReaction = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyViewerReactable on Reactable {
        ...ReactionViewerRelayGroups
      }
    `,
    reactable,
  )
  const dataSubIssues = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyViewerSubIssues on Issue {
        ...useCanEditSubIssues
        ...useHasSubIssues
        ...AddSubIssueButtonGroup @arguments(fetchSubIssues: false)
      }
    `,
    subIssues,
  )
  const dataPinnedComment = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyViewerPinnedComment on Issue {
        ...PinnedCommentDisplay
      }
    `,
    pinnedComment,
  )
  const dataPinnedCommentSecondary = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueBodyViewerPinnedCommentSecondary on Issue {
        ...PinnedCommentDisplaySecondary
      }
    `,
    pinnedCommentSecondary,
  )

  const hasSubIssues = useHasSubIssues(dataSubIssues)
  const canEditSubIssues = useCanEditSubIssues(dataSubIssues)
  const showAddSubIssueButton = canEditSubIssues && dataSubIssues && !hasSubIssues && !isReadonly

  // Pinned Comments
  const showPinnedComment = !!dataPinnedComment

  const onSave = useCallback(
    (newBody: string, onCompleted: () => void, onError: () => void) => {
      commitUpdateIssueBodyMutation({
        environment,
        input: {issueId: data.id, body: newBody, bodyVersion},
        onCompleted,
        onError,
      })
    },
    [bodyVersion, environment, data.id],
  )

  const {addToast} = useToastContext()

  /**
   * * Handles conversion completion timing - can't immediately set isConverting to false
   * because UI updates (rendering converted items as links) happen asynchronously.
   * Uses MutationObserver to wait for DOM changes ensuring loading state persists
   * until visual conversion is complete.
   **/
  const handleConversionComplete = useCallback(
    (
      id: string,
      setIsConverting: (isConverting: boolean) => void,
      onCompletedCallback?: () => void,
      convertingToSubIssue = false,
    ) => {
      let timeoutId: ReturnType<typeof safeSetTimeout> | null = null

      const completeMutation = () => {
        setIsConverting(false)
        onCompletedCallback?.()

        if (timeoutId) {
          safeClearTimeout(timeoutId)
        }
      }

      if (convertingToSubIssue) {
        // No need for observation as the rerender is triggered immediately
        requestAnimationFrame(() => {
          completeMutation()
        })
        return
      }

      const observer = new MutationObserver(mutationsList => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const safeHTMLBox = document.querySelector(`#checkbox-item-${id}`)

            if (safeHTMLBox?.querySelector('a')) {
              observer.disconnect()
              completeMutation()
              return
            }
          }
        }
      })

      // Observing the closest element that doesn't rerender on mutation complete
      const issueContainer = document.getElementById('issue-body-viewer')
      if (issueContainer) {
        observer.observe(issueContainer, {childList: true, subtree: true})
      } else {
        completeMutation()
        return
      }

      // If we don't find the newly inserted link, disconnect
      timeoutId = safeSetTimeout(() => {
        observer.disconnect()
        completeMutation()
      }, 3000)
    },
    [safeSetTimeout, safeClearTimeout],
  )

  const onConvertToIssue = useCallback(
    (
      task: TaskItem,
      setIsConverting: (converting: boolean) => void,
      onCompletedCallback?: () => void,
      onErrorCallback?: (error: Error) => void,
    ) => {
      if (!(repositoryId && onIssueEditStateChange)) return

      setIsConverting(true)
      commitCreateIssueFromChecklistItemMutation({
        environment,
        input: {parentIssueId: data.id, repositoryId, title: task.title, position: task.position},
        onCompleted: () => {
          onIssueEditStateChange?.(true)
          handleConversionComplete(task.id, setIsConverting, onCompletedCallback)
        },
        onError: error => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: ERRORS.couldNotConvertIssue})
          setIsConverting(false)
          onErrorCallback?.(error)
        },
      })
    },
    [repositoryId, onIssueEditStateChange, environment, data.id, addToast, handleConversionComplete],
  )

  const onConvertToSubIssue = useMemo(
    () =>
      (
        task: TaskItem,
        setIsConverting: (converting: boolean) => void,
        onCompletedCallback?: () => void,
        onErrorCallback?: (error: Error) => void,
      ) => {
        if (!(repositoryId && onIssueEditStateChange)) return

        setIsConverting(true)
        commitCreateSubIssueFromChecklistItemMutation({
          environment,
          input: {
            parentIssueId: data.id,
            repositoryId,
            body: markdown,
            position: task.position as [number, number],
          },
          onCompleted: () => {
            onIssueEditStateChange?.(true)
            handleConversionComplete(task.id, setIsConverting, onCompletedCallback, true)
          },
          onError: error => {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({type: 'error', message: ERRORS.couldNotConvertIssue})
            setIsConverting(false)
            onErrorCallback?.(error)
          },
        })
      },
    [repositoryId, onIssueEditStateChange, environment, data.id, addToast, markdown, handleConversionComplete],
  )

  const modernUx = isFeatureEnabled('issues_modern_ux')

  return (
    <div
      className={clsx(styles.IssueBody, modernUx && styles.IssueBodyModernUx)}
      ref={issueBodyRef}
      id="issue-body-viewer"
      data-testid="issue-body-viewer"
    >
      <IssueMarkdownViewer
        html={html}
        markdown={markdown}
        viewerCanUpdate={viewerCanUpdate}
        onSave={onSave}
        onLinkClick={onLinkClick}
        onConvertToIssue={onConvertToIssue}
        onConvertToSubIssue={onConvertToSubIssue}
      />
      <div className={styles.IssueBodyTaskList}>
        {showAddSubIssueButton && (
          <div className={styles.IssueBodySubIssueButtonContainer}>
            <AddSubIssueButtonGroup issue={dataSubIssues} insideSidePanel={insideSidePanel} />
          </div>
        )}
        {issueUrl && (isFeatureEnabled('issues_open_in_tool_links') || modernUx) && (
          <CodingAssistantButton
            url={issueUrl}
            buttonLabel="Plan issue"
            promptBuilder={(url: string) => {
              const match = url.match(/\/issues\/(\d+)$/)
              const issueNumber = match?.[1] ?? ''
              return `Create a plan for the following GitHub issue: ${url}. Before generating the plan, ask the user which repositories to explore for relevant implementation context. If the user does not specify any, explore related repositories under the same owner or organization. Plan generation should take place in the issue's repository. Store the plan in .github/plans/issue-${issueNumber}.md, creating the .github/plans directory if it does not exist. Before creating a linked branch or pushing any file changes, confirm with the user that they would like to proceed. Use gh issue develop to create and link a branch for this issue. Then, in the issue repository, check out that linked branch, commit the new file, and push it.`
            }}
            size="small"
            className="mr-2"
            showIcon={false}
          />
        )}
        {!isReadonly && (
          <Suspense fallback={<ReactionViewerAnchor />}>
            <ReactionViewerRelay subjectId={data.id} canReact={isLoggedIn() && !locked} reactionGroups={dataReaction} />
          </Suspense>
        )}
      </div>
      {showPinnedComment && (
        <PinnedCommentDisplay issue={dataPinnedComment} secondaryKey={dataPinnedCommentSecondary} />
      )}
    </div>
  )
}
